import React, { useRef, useState } from 'react';
import { ElevationPoint } from '../../services/gpxTraceService';

interface HoverPosition { lat: number; lon: number; }

interface Props {
    profile: ElevationPoint[];
    onHover?: (pos: HoverPosition | null) => void;
}

const PAD = { top: 12, right: 16, bottom: 32, left: 48 };

const ElevationChart: React.FC<Props> = ({ profile, onHover }) => {
    const overlayRef = useRef<SVGRectElement>(null);
    const [tooltip, setTooltip] = useState<{ x: number; y: number; dist: number; elev: number } | null>(null);

    const valid = profile.filter(p => p.elevation !== null) as { distance_km: number; elevation: number; lat: number; lon: number }[];
    if (valid.length < 2) return <p style={{ textAlign: 'center', color: '#aaa', padding: '16px 0' }}>Aucune donn&eacute;e d&apos;altitude</p>;

    const minElev = Math.min(...valid.map(p => p.elevation));
    const maxElev = Math.max(...valid.map(p => p.elevation));
    const maxDist = valid[valid.length - 1].distance_km;

    const W = 600;
    const H = 120;
    const innerW = W - PAD.left - PAD.right;
    const innerH = H - PAD.top - PAD.bottom;

    const elevRange = maxElev - minElev || 1;

    const toX = (d: number) => PAD.left + (d / maxDist) * innerW;
    const toY = (e: number) => PAD.top + innerH - ((e - minElev) / elevRange) * innerH;

    // Build SVG path
    const points = valid.map(p => `${toX(p.distance_km).toFixed(1)},${toY(p.elevation).toFixed(1)}`).join(' ');
    const areaPath = `M ${toX(valid[0].distance_km)},${toY(valid[0].elevation)} ` +
        valid.slice(1).map(p => `L ${toX(p.distance_km).toFixed(1)},${toY(p.elevation).toFixed(1)}`).join(' ') +
        ` L ${toX(valid[valid.length - 1].distance_km)},${PAD.top + innerH} L ${toX(valid[0].distance_km)},${PAD.top + innerH} Z`;

    // Y axis labels
    const yTicks = [minElev, minElev + elevRange / 2, maxElev].map(v => Math.round(v));

    // X axis labels (4 ticks max)
    const xTickCount = Math.min(4, Math.floor(maxDist));
    const xTicks: number[] = [];
    for (let i = 0; i <= xTickCount; i++) {
        xTicks.push(parseFloat(((maxDist * i) / xTickCount).toFixed(2)));
    }

    // Les events souris sont attachés au <rect> couvrant exactement la zone du graphique.
    // La position de la souris relative à ce rect correspond directement à la zone de données,
    // sans avoir à soustraire un padding — ce qui élimine le décalage dans les zones blanches.
    const handleMouseMove = (e: React.MouseEvent<SVGRectElement>) => {
        const rect = overlayRef.current?.getBoundingClientRect();
        if (!rect) return;
        const relX = ((e.clientX - rect.left) / rect.width) * innerW;
        const dist = (relX / innerW) * maxDist;
        let closest = valid[0];
        let minDiff = Math.abs(valid[0].distance_km - dist);
        for (const p of valid) {
            const diff = Math.abs(p.distance_km - dist);
            if (diff < minDiff) { minDiff = diff; closest = p; }
        }
        setTooltip({
            x: toX(closest.distance_km),
            y: toY(closest.elevation),
            dist: closest.distance_km,
            elev: closest.elevation,
        });
        onHover?.({ lat: closest.lat, lon: closest.lon });
    };

    const handleMouseLeave = () => {
        setTooltip(null);
        onHover?.(null);
    };

    return (
        <svg
            viewBox={`0 0 ${W} ${H}`}
            style={{ width: '100%', height: '100%', display: 'block' }}
        >
            {/* Filled area */}
            <defs>
                <linearGradient id="elevGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#1976d2" stopOpacity="0.35" />
                    <stop offset="100%" stopColor="#1976d2" stopOpacity="0.05" />
                </linearGradient>
            </defs>
            <path d={areaPath} fill="url(#elevGrad)" />
            {/* Line */}
            <polyline points={points} fill="none" stroke="#1976d2" strokeWidth="2" strokeLinejoin="round" />

            {/* Y axis */}
            {yTicks.map(v => (
                <g key={v}>
                    <line
                        x1={PAD.left} y1={toY(v)}
                        x2={PAD.left + innerW} y2={toY(v)}
                        stroke="#e0e0e0" strokeWidth="1"
                    />
                    <text x={PAD.left - 4} y={toY(v) + 4} textAnchor="end" fontSize="10" fill="#888">{v}m</text>
                </g>
            ))}

            {/* X axis ticks */}
            {xTicks.map(d => (
                <g key={d}>
                    <line
                        x1={toX(d)} y1={PAD.top + innerH}
                        x2={toX(d)} y2={PAD.top + innerH + 4}
                        stroke="#aaa" strokeWidth="1"
                    />
                    <text x={toX(d)} y={PAD.top + innerH + 14} textAnchor="middle" fontSize="10" fill="#888">{d} km</text>
                </g>
            ))}

            {/* Zone interactive — couvre exactement l'aire du graphique */}
            <rect
                ref={overlayRef}
                x={PAD.left} y={PAD.top}
                width={innerW} height={innerH}
                fill="transparent"
                style={{ cursor: 'crosshair' }}
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
            />

            {/* Tooltip — pointer-events none pour éviter le clignotement */}
            {tooltip && (() => {
                const label = `${tooltip.dist.toFixed(2)} km · ${Math.round(tooltip.elev)} m`;
                const boxW = Math.max(80, label.length * 6.5 + 12);
                const boxH = 22;
                const boxX = Math.min(Math.max(tooltip.x - boxW / 2, PAD.left), PAD.left + innerW - boxW);
                const boxY = Math.max(tooltip.y - boxH - 8, PAD.top);
                return (
                    <g pointerEvents="none">
                        <line
                            x1={tooltip.x} y1={PAD.top}
                            x2={tooltip.x} y2={PAD.top + innerH}
                            stroke="#333" strokeWidth="1" strokeDasharray="3,2"
                        />
                        <circle cx={tooltip.x} cy={tooltip.y} r={4} fill="#1976d2" stroke="white" strokeWidth="1.5" />
                        <rect
                            x={boxX} y={boxY}
                            width={boxW} height={boxH} rx={4}
                            fill="rgba(0,0,0,0.72)"
                        />
                        <text
                            x={boxX + boxW / 2}
                            y={boxY + boxH / 2 + 4}
                            textAnchor="middle" fontSize="11" fill="white"
                        >
                            {label}
                        </text>
                    </g>
                );
            })()}
        </svg>
    );
};

export default ElevationChart;
